import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import IntegrationSettingsTabs from "../../../../Components/Common/IntegrationSettingsTabs";
import { helpUrlForRoute } from "../../../../constants/helpRouteMap";
import {
  apiErrorMessage,
  deleteWebhook,
  getWebhookDeliveries,
  getWebhooks,
  testWebhook,
  WebhookDelivery,
  WebhookEndpoint,
} from "../../../../helpers/webhooks_helper";

type LocationSecretState = { revealedSecret?: string } | null;

/**
 * Lista de webhooks outbound (Conta) — DOC 09 § 6.1 · 06 § 6.9.
 * Secret 1× chega via location.state após create/rotate.
 */
const WebhookList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = i18n.language || "pt-BR";

  document.title = t("settings.webhooks.documentTitle");

  const helpHref = helpUrlForRoute("/settings/webhooks", locale);

  const [items, setItems] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<WebhookEndpoint | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [recentDeliveries, setRecentDeliveries] = useState<
    Array<WebhookDelivery & { webhook_url?: string }>
  >([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);

  useEffect(() => {
    const state = location.state as LocationSecretState;
    if (state?.revealedSecret) {
      setRevealedSecret(state.revealedSecret);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getWebhooks();
      if (!res?.success) {
        setError(t("settings.webhooks.error.load"));
        setItems([]);
        return;
      }
      const rows = res.data || [];
      setItems(rows);

      // Histórico leve: até 3 endpoints × 5 deliveries (aba Integrações).
      setDeliveriesLoading(true);
      try {
        const active = rows.filter((w) => w.is_active).slice(0, 3);
        const batches = await Promise.all(
          active.map(async (w) => {
            try {
              const d = await getWebhookDeliveries(w.id);
              return (d.data || []).slice(0, 5).map((row) => ({
                ...row,
                webhook_url: w.url,
              }));
            } catch {
              return [];
            }
          })
        );
        const flat = batches.flat().sort((a, b) => {
          const ta = a.created_at || "";
          const tb = b.created_at || "";
          return tb.localeCompare(ta);
        });
        setRecentDeliveries(flat.slice(0, 10));
      } finally {
        setDeliveriesLoading(false);
      }
    } catch (e) {
      setError(apiErrorMessage(e, t("settings.webhooks.error.load")));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onTest = async (row: WebhookEndpoint) => {
    setBusyId(row.id);
    try {
      const res = await testWebhook(row.id);
      toast.success(res?.message || t("settings.webhooks.toast.tested"), {
        position: "top-center",
      });
    } catch (e) {
      toast.error(apiErrorMessage(e, t("settings.webhooks.error.test")), {
        position: "top-center",
      });
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusyId(deleting.id);
    try {
      const res = await deleteWebhook(deleting.id);
      toast.success(res?.message || t("settings.webhooks.toast.deleted"), {
        position: "top-center",
      });
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("settings.webhooks.error.delete")), {
        position: "top-center",
      });
    } finally {
      setBusyId(null);
    }
  };

  const copySecret = async () => {
    if (!revealedSecret) return;
    try {
      await navigator.clipboard.writeText(revealedSecret);
      toast.success(t("settings.webhooks.reveal.copied"), {
        position: "top-center",
      });
    } catch {
      toast.error(t("settings.webhooks.reveal.copyFailed"), {
        position: "top-center",
      });
    }
  };

  const isEmpty = !loading && items.length === 0;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("settings.webhooks.title")}
          actions={
            <PageHelpLink
              href={helpHref}
              label={t("settings.webhooks.help")}
            />
          }
        />
        <ToastContainer />
        <IntegrationSettingsTabs />

        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {revealedSecret && (
          <Alert color="warning" className="mb-3">
            <h5 className="alert-heading">{t("settings.webhooks.reveal.title")}</h5>
            <p className="mb-2">{t("settings.webhooks.reveal.warning")}</p>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <code className="flex-grow-1 text-break bg-light p-2 rounded">
                {revealedSecret}
              </code>
              <Button color="primary" size="sm" onClick={() => void copySecret()}>
                {t("settings.webhooks.reveal.copy")}
              </Button>
              <Button
                color="soft-secondary"
                size="sm"
                onClick={() => setRevealedSecret(null)}
              >
                {t("settings.webhooks.reveal.done")}
              </Button>
            </div>
          </Alert>
        )}

        <DismissiblePageTip tipId="webhooks">
          <p className="mb-1">{t("settings.webhooks.tip")}</p>
          <p className="mb-0 text-muted small">{t("settings.webhooks.tipPortal")}</p>
        </DismissiblePageTip>

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <h5 className="card-title mb-0">{t("settings.webhooks.listTitle")}</h5>
                <Button
                  color="primary"
                  tag={Link}
                  to="/settings/webhooks/new"
                >
                  {t("settings.webhooks.create")}
                </Button>
              </CardHeader>
              <CardBody>
                {loading && (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                  </div>
                )}

                {isEmpty && (
                  <div className="text-center py-5">
                    <h5>{t("settings.webhooks.empty.title")}</h5>
                    <p className="text-muted mb-2">
                      {t("settings.webhooks.empty.description")}
                    </p>
                    <p className="mb-3">
                      <a
                        href="https://docs.brenvio.com.br/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t("settings.webhooks.empty.portalLink")}
                      </a>
                    </p>
                    <Button
                      color="primary"
                      tag={Link}
                      to="/settings/webhooks/new"
                    >
                      {t("settings.webhooks.empty.cta")}
                    </Button>
                  </div>
                )}

                {!loading && items.length > 0 && (
                  <div className="table-responsive">
                    <Table className="align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("settings.webhooks.table.url")}</th>
                          <th>{t("settings.webhooks.table.events")}</th>
                          <th>{t("settings.webhooks.table.status")}</th>
                          <th className="text-end">
                            {t("settings.webhooks.table.actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((row) => (
                          <tr key={row.id}>
                            <td className="text-break" style={{ maxWidth: 320 }}>
                              {row.url}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {(row.events || []).map((ev) => (
                                  <Badge key={ev} color="soft-info" className="me-0">
                                    {t(`settings.webhooks.events.${ev}`, {
                                      defaultValue: ev,
                                    })}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td>
                              {row.is_active ? (
                                <Badge color="success">
                                  {t("settings.webhooks.status.active")}
                                </Badge>
                              ) : (
                                <Badge color="secondary">
                                  {t("settings.webhooks.status.paused")}
                                </Badge>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="d-inline-flex flex-wrap gap-1 justify-content-end">
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  tag={Link}
                                  to={`/settings/webhooks/${row.id}`}
                                >
                                  {t("settings.webhooks.edit")}
                                </Button>
                                <Button
                                  color="soft-success"
                                  size="sm"
                                  disabled={
                                    !row.is_active || busyId === row.id
                                  }
                                  onClick={() => void onTest(row)}
                                >
                                  {busyId === row.id ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    t("settings.webhooks.test")
                                  )}
                                </Button>
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  disabled={busyId === row.id}
                                  onClick={() => setDeleting(row)}
                                >
                                  {t("settings.webhooks.delete")}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>

            <Card className="mt-3">
              <CardHeader>
                <h5 className="card-title mb-0">
                  {t("settings.webhooks.recentDeliveries.title")}
                </h5>
                <p className="text-muted small mb-0">
                  {t("settings.webhooks.recentDeliveries.hint")}
                </p>
              </CardHeader>
              <CardBody>
                {deliveriesLoading ? (
                  <div className="text-center py-3">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : recentDeliveries.length === 0 ? (
                  <p className="text-muted mb-0">
                    {t("settings.webhooks.recentDeliveries.empty")}
                  </p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-sm table-nowrap align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("settings.webhooks.recentDeliveries.colEvent")}</th>
                          <th>{t("settings.webhooks.recentDeliveries.colStatus")}</th>
                          <th>{t("settings.webhooks.recentDeliveries.colUrl")}</th>
                          <th>{t("settings.webhooks.recentDeliveries.colWhen")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentDeliveries.map((d) => (
                          <tr key={d.id}>
                            <td>{d.event}</td>
                            <td>
                              <Badge
                                color={
                                  d.status === "delivered"
                                    ? "success"
                                    : d.status === "failed"
                                      ? "danger"
                                      : "warning"
                                }
                              >
                                {d.status}
                              </Badge>
                              {d.last_http_status ? (
                                <span className="text-muted small ms-1">
                                  HTTP {d.last_http_status}
                                </span>
                              ) : null}
                            </td>
                            <td
                              className="text-truncate small text-muted"
                              style={{ maxWidth: 220 }}
                            >
                              {d.webhook_url || "—"}
                            </td>
                            <td className="small text-muted">
                              {d.created_at
                                ? new Date(d.created_at).toLocaleString(locale)
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={!!deleting} toggle={() => setDeleting(null)} centered>
        <ModalHeader toggle={() => setDeleting(null)}>
          {t("settings.webhooks.confirmDelete.title")}
        </ModalHeader>
        <ModalBody>{t("settings.webhooks.confirmDelete.body")}</ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDeleting(null)}>
            {t("settings.webhooks.confirmDelete.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={busyId !== null}
            onClick={() => void confirmDelete()}
          >
            {t("settings.webhooks.confirmDelete.confirm")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default WebhookList;
